// Selling a membership in the office (memberships.sell; office-history prototype's "Offer the
// membership"): a plan, the location it covers, and the open job's draft invoice it goes on, as the
// action requires. Its revenue is deferred and its included visits come due for the office to book.
// Opened from the customer record or from Memberships with openSellMembership(customerId?).

import { useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import { dayText } from '../books/model.ts';
import { Money, errorText } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import { newId } from '../shared/ids.ts';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import type { Customer } from '../types.ts';
import {
  includedText,
  renewalConsent,
  sellable,
  termText,
  type DraftInvoice,
  type Plan,
  type SellablePlan,
  type Renewal,
  type RenewalTerms,
} from './model.ts';
import { closeSlot, onLeave, openSlot, type Slot } from '../shared/sheet-slot.ts';
import { membershipSold } from './sold.ts';
import '../review/review.css';
import './sell.css';

let opener: ((customerId: string | null) => void) | null = null;

/** Opens the sell dialog, for a customer or to find one. A no-op until the app has mounted the host. */
export function openSellMembership(customerId?: string): void {
  opener?.(customerId ?? null);
}

/** Mounted once by the app. */
export function SellMembershipHost({ today, onSold }: { today: string; onSold?: () => void }) {
  // Each opening is its own: a dialog closing late never shuts the one opened again at once.
  const [open, setOpen] = useState<Slot<string | null> | null>(null);
  useEffect(() => {
    opener = (customerId) => setOpen(openSlot(customerId));
    // leaving the page it's over (Back, a link) closes it: the view follows the address
    const stop = onLeave(window, () => setOpen(null));
    return () => {
      opener = null;
      stop();
    };
  }, []);
  if (!open) return null;
  return (
    <SellDialog
      key={open.at}
      customerId={open.value}
      today={today}
      onClose={() => setOpen((cur) => closeSlot(cur, open.at))}
      onSold={() => {
        membershipSold(); // Members and the like read again (QA-531)
        onSold?.();
      }}
    />
  );
}

interface JobGet {
  id: string;
  number: string;
  summary: string;
  locationId: string;
  address: string;
  invoice: { id: string; number: string; status: string } | null;
}

function SellDialog({
  customerId: given,
  today,
  onClose,
  onSold,
}: {
  customerId: string | null;
  today: string;
  onClose: () => void;
  onSold: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [customerId, setCustomerId] = useState(given);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [drafts, setDrafts] = useState<DraftInvoice[] | null>(null);
  const [plans, setPlans] = useState<SellablePlan[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [planId, setPlanId] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [startsOn, setStartsOn] = useState(today);
  const [firstVisitOn, setFirstVisitOn] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  // Renewing by itself needs the customer's yes to the terms shown, word for word (F-27)
  const [terms, setTerms] = useState<RenewalTerms | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sold, setSold] = useState<string | null>(null);
  // One membership id per attempt: a lost answer retried sells it once (the action replays by id).
  const [membershipId] = useState(newId);
  const save = useSaveKey(
    `${membershipId}:${planId}:${invoiceId}:${locationId}:${startsOn}:${firstVisitOn}:${autoRenew}:${agreed ? terms?.version : ''}`,
  );

  // the plan's renewal terms, read when renewing is ticked (and again for another plan)
  useEffect(() => {
    setTerms(null);
    setAgreed(false);
    setTermsError(null);
    if (!autoRenew || !planId) return;
    let current = true;
    client
      .call<RenewalTerms>('memberships.renewalTerms', { typeId: planId })
      .then((t) => current && setTerms(t))
      .catch((e: unknown) => current && setTermsError(errorText(e, 'Could not load the renewal terms.')));
    return () => {
      current = false;
    };
  }, [autoRenew, planId]);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    client
      .call<{ plans: Plan[] }>('membershipTypes.list', {})
      .then((r) => {
        // a plan imported without its term, visits or price isn't sold until they're set
        const live = r.plans.filter((p) => p.active).filter(sellable);
        setPlans(live);
        if (live.length === 1) setPlanId(live[0]!.id);
      })
      .catch((e: unknown) => setLoadError(errorText(e, 'Could not load the plans.')));
  }, []);

  useEffect(() => {
    if (!customerId) return;
    setCustomer(null);
    setDrafts(null);
    void (async () => {
      try {
        const [c, js] = await Promise.all([
          client.call<Customer>('customers.get', { id: customerId }),
          client.call<{ jobs: { id: string }[] }>('jobs.list', {
            customerId,
            status: ['open', 'in_progress', 'awaiting_review'],
            limit: 10,
          }),
        ]);
        setCustomer(c);
        const jobs = await Promise.all(js.jobs.map((j) => client.call<JobGet>('jobs.get', { id: j.id })));
        const open = jobs
          .filter((j) => j.invoice?.status === 'draft')
          .map((j) => ({
            invoiceId: j.invoice!.id,
            invoiceNumber: j.invoice!.number,
            jobId: j.id,
            jobNumber: j.number,
            summary: j.summary,
            locationId: j.locationId,
            address: j.address,
          }));
        setDrafts(open);
        if (open[0]) {
          setInvoiceId(open[0].invoiceId);
          setLocationId(open[0].locationId);
        } else setLocationId(c.locations[0]?.id ?? '');
      } catch (e) {
        setLoadError(errorText(e, 'Could not load the customer.'));
      }
    })();
  }, [customerId]);

  const plan = plans?.find((p) => p.id === planId);
  const renewal = renewalConsent(autoRenew, terms, agreed);
  const ready = !!customer && !!plan && !!invoiceId && !!locationId && !!startsOn && renewal.ready;
  const sell = async () => {
    if (!ready || !customer) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ renewal?: Renewal }>(
        'memberships.sell',
        {
          membershipId,
          typeId: planId,
          customerId: customer.id,
          locationId,
          invoiceId,
          startsOn,
          ...(firstVisitOn ? { firstVisitOn } : {}),
          autoRenew,
          ...(renewal.consent ? { renewalConsent: renewal.consent } : {}),
        },
        { idempotencyKey: save.key },
      );
      const inv = drafts?.find((x) => x.invoiceId === invoiceId);
      setSold(
        `${customer.displayName} is now on ${plan!.name}. Its line is on invoice ${inv?.invoiceNumber ?? ''}${plan!.visitsPerTerm ? '; the included visits come due in To book' : ''}.${r.renewal?.notice ? ` ${r.renewal.notice}` : ''}`,
      );
      onSold();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog ref={ref} className="sheet ask sell-dialog" aria-labelledby="sell-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && !sold) void sell();
        }}
      >
        <h3 id="sell-title">
          {customer ? `Offer a membership to ${customer.displayName}` : 'Sell a membership'}
        </h3>
        {sold ? (
          <>
            <p className="notice ok" role="status">
              {sold}
            </p>
            <div className="row end">
              <button type="button" className="btn primary" onClick={() => ref.current?.close()}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="muted">
              It goes on an open job’s draft invoice. The revenue is spread over the term, visit by visit.
            </p>
            {loadError && <p className="error">{loadError}</p>}
            {!customerId && <FindCustomer onPick={setCustomerId} />}
            {customerId && !customer && !loadError && <Loading error={null} what="the customer" />}
            {customer && plans && (
              <>
                <label>
                  Plan
                  <select className="field" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                    <option value="">Pick a plan…</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
                {plans.length === 0 && (
                  <p className="notice warn">
                    There are no plans to sell yet. The owner or a manager adds them in Plans.
                  </p>
                )}
                {plan && (
                  <div className="pebbles sell-facts">
                    <span className="pebble">
                      <span>Price</span>
                      <b>
                        <Money cents={plan.priceCents} />
                      </b>
                    </span>
                    <span className="pebble">
                      <span>Term</span>
                      <b>{termText(plan.termMonths)}</b>
                    </span>
                    <span className="pebble">
                      <span>Includes</span>
                      <b>{includedText(plan.visitsPerTerm)}</b>
                    </span>
                  </div>
                )}
                {drafts === null ? (
                  <Loading error={null} what="the open jobs" />
                ) : drafts.length === 0 ? (
                  <p className="notice warn">
                    {customer.displayName} has no open job with a draft invoice. Book a visit first; the
                    membership goes on its invoice.
                  </p>
                ) : (
                  <label>
                    On the invoice of
                    <select
                      className="field"
                      value={invoiceId}
                      onChange={(e) => {
                        setInvoiceId(e.target.value);
                        const d = drafts.find((x) => x.invoiceId === e.target.value);
                        if (d) setLocationId(d.locationId);
                      }}
                    >
                      {drafts.map((d) => (
                        <option key={d.invoiceId} value={d.invoiceId}>
                          Job #{d.jobNumber} · {d.summary.slice(0, 60)} · invoice {d.invoiceNumber}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {customer.locations.length > 1 && (
                  <label>
                    The location it covers
                    <select
                      className="field"
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value)}
                    >
                      {customer.locations.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name ? `${l.name} · ` : ''}
                          {l.line1}, {l.city}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div className="fgrid">
                  <label>
                    Starts
                    <input
                      className="field"
                      type="date"
                      value={startsOn}
                      onChange={(e) => setStartsOn(e.target.value)}
                    />
                  </label>
                  {plan && plan.visitsPerTerm > 0 && (
                    <label>
                      First visit due (optional)
                      <input
                        className="field"
                        type="date"
                        value={firstVisitOn}
                        min={startsOn}
                        onChange={(e) => setFirstVisitOn(e.target.value)}
                      />
                    </label>
                  )}
                </div>
                {plan && plan.visitsPerTerm > 0 && !firstVisitOn && (
                  <p className="muted small">The first visit comes due on {dayText(startsOn)}.</p>
                )}
                <label className="checkrow">
                  <input
                    type="checkbox"
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                  />{' '}
                  Renew automatically each term (the customer agrees to the terms below)
                </label>
                {autoRenew && (
                  <div className="renewal-terms" role="group" aria-label="Renewal terms">
                    {termsError ? (
                      <p className="error small">{termsError}</p>
                    ) : !terms ? (
                      <p className="muted small">Getting the renewal terms…</p>
                    ) : (
                      <>
                        <p className="small renewal-text">{terms.text}</p>
                        <label className="checkrow">
                          <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                          />{' '}
                          The customer agrees to these terms
                        </label>
                      </>
                    )}
                  </div>
                )}
                {customer.doNotContact && (
                  <p className="muted small">
                    {customer.displayName} is do-not-contact: no welcome or renewal messages go out. Call them
                    instead.
                  </p>
                )}
              </>
            )}
            <div className="row end">
              <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
                Cancel
              </button>
              <SaveButton
                busy={busy}
                busyLabel="Selling…"
                error={error}
                disabled={!ready}
                onClick={() => void sell()}
              >
                Sell the membership
              </SaveButton>
            </div>
          </>
        )}
      </form>
    </dialog>
  );
}

function FindCustomer({ onPick }: { onPick: (id: string) => void }) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<{ id: string; displayName: string; city: string | null }[]>([]);
  useEffect(() => {
    if (!query.trim()) return setFound([]);
    const t = setTimeout(() => {
      client
        .call<{ customers: { id: string; displayName: string; city: string | null }[] }>('customers.list', {
          query: query.trim(),
          limit: 8,
        })
        .then((r) => setFound(r.customers))
        .catch(() => setFound([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);
  return (
    <div className="sell-find">
      <label>
        Customer
        <input
          className="field"
          type="search"
          placeholder="Name, number or street"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {found.length > 0 && (
        <ul className="found" aria-label="Customers">
          {found.map((c) => (
            <li key={c.id}>
              <button type="button" className="row-btn" onClick={() => onPick(c.id)}>
                <span className="who-col">
                  <b>{c.displayName}</b>
                  {c.city && <span className="muted small"> · {c.city}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
